import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Upload,
  FileText,
  Calendar,
  DollarSign,
  Users,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle,
  Download,
  X,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { maturityPaymentSchedulesAPI, maturityDistributionRequestsAPI, bondsAPI, holdingsAPI, formatDate, formatCurrency } from '../../services/api';
import { toNumberSafe } from '../../utils/moneyUtils';
import { useAuth } from '../../contexts/AuthContext';

const statusBadgeConfig = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  'pending_approval': { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  processing: { label: 'Processing', color: 'bg-purple-100 text-purple-800', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle }
};

const renderStatusBadge = (schedule) => {
  if (!schedule) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <Clock className="w-3.5 h-3.5 mr-1" />
        No Schedule
      </span>
    );
  }
  const { status } = schedule;
  const config = statusBadgeConfig[status] || statusBadgeConfig.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3.5 h-3.5 mr-1" />
      {config.label}
    </span>
  );
};

// Helper function to process holdings and create Primary Subscriber list
// allHoldings: all holdings (including inactive) - used to find PS info
// activeHoldings: only active holdings - used to calculate amounts
// Returns: Array of Primary Subscribers with aggregated payment amounts
const processHoldingsToInvestors = (allHoldings, activeHoldings) => {
  if (!Array.isArray(activeHoldings) || activeHoldings.length === 0) {
    return [];
  }

  const allHoldingsArray = Array.isArray(allHoldings) ? allHoldings : [];
  const primarySubscriberMap = new Map(); // primarySubscriberId -> { PS info, aggregatedAmount, directInvestors[], custodians[] }

  // First pass: Identify Primary Subscribers from all holdings
  // Primary Subscriber: custodianId = null AND primarySubscriberId = null
  allHoldingsArray.forEach((holding) => {
    const custodianId = holding.custodianId?._id || holding.custodianId;
    const primarySubscriberId = holding.primarySubscriberId?._id || holding.primarySubscriberId;
    const investorId = holding.investorId?._id || holding.investorId;
    const holderId = holding.holderId?._id || holding.holderId;
    const investorSnapshot = holding.investorSnapshot || {};
    const holderSnapshot = holding.holderSnapshot || {};

    // Check if this is a Primary Subscriber holding
    if (!custodianId && !primarySubscriberId) {
      const psId = investorId || holderId;
      if (psId) {
        const psKey = String(psId);
        if (!primarySubscriberMap.has(psKey)) {
          primarySubscriberMap.set(psKey, {
            investorId: psKey,
            name: investorSnapshot.name || holderSnapshot.name || investorSnapshot.email || holderSnapshot.email || 'Unknown Primary Subscriber',
            email: investorSnapshot.email || holderSnapshot.email || '—',
            lei: investorSnapshot.lei || holderSnapshot.lei || '',
            tokenHoldings: 0, // Aggregated from direct investors and sub-investors
            paymentAmount: 0, // Will be calculated later
            status: 'pending',
            isPrimarySubscriber: true,
            directInvestors: [], // Track direct investors under this PS
            custodians: [] // Track custodians under this PS
          });
        }
      }
    }
  });

  // Second pass: Process active holdings to aggregate amounts under each Primary Subscriber
  activeHoldings.forEach((holding) => {
    const remainingAmount = toNumberSafe(holding.remainingTokenAmount || 0, 0);
    if (remainingAmount <= 0) return; // Skip zero holdings

    const custodianId = holding.custodianId?._id || holding.custodianId;
    const primarySubscriberId = holding.primarySubscriberId?._id || holding.primarySubscriberId;
    const investorId = holding.investorId?._id || holding.investorId;
    const holderId = holding.holderId?._id || holding.holderId;
    const investorSnapshot = holding.investorSnapshot || {};
    const holderSnapshot = holding.holderSnapshot || {};

    // Skip Primary Subscriber's own holdings (they should have remainingTokenAmount = 0)
    if (!custodianId && !primarySubscriberId) {
      return; // This is a PS holding, skip it
    }

    // If this holding has a primarySubscriberId, aggregate it under that PS
    if (primarySubscriberId) {
      const psKey = String(primarySubscriberId);
      const ps = primarySubscriberMap.get(psKey);
      
      if (ps) {
        // Add to aggregated token holdings
        ps.tokenHoldings = toNumberSafe(ps.tokenHoldings, 0) + remainingAmount;

        // Track direct investors and custodians for submission
        if (!custodianId) {
          // This is a direct investor (or custodian) under the PS
          const isCustodian = investorSnapshot.isCustodian || holderSnapshot.isCustodian;
          const holderKey = String(investorId || holderId);
          
          if (isCustodian) {
            // Track custodian
            const existingCustodian = ps.custodians.find(c => c.investorId === holderKey);
            if (!existingCustodian) {
              ps.custodians.push({
                investorId: holderKey,
                name: investorSnapshot.name || holderSnapshot.name || investorSnapshot.email || holderSnapshot.email || 'Unknown Custodian',
                email: investorSnapshot.email || holderSnapshot.email || '—',
                tokenHoldings: remainingAmount,
                isCustodian: true
              });
            } else {
              existingCustodian.tokenHoldings = toNumberSafe(existingCustodian.tokenHoldings, 0) + remainingAmount;
            }
          } else {
            // Track direct investor
            const existingDirect = ps.directInvestors.find(d => d.investorId === holderKey);
            if (!existingDirect) {
              ps.directInvestors.push({
                investorId: holderKey,
                name: investorSnapshot.name || holderSnapshot.name || investorSnapshot.email || holderSnapshot.email || 'Unknown Investor',
                email: investorSnapshot.email || holderSnapshot.email || '—',
                tokenHoldings: remainingAmount,
                isCustodian: false
              });
            } else {
              existingDirect.tokenHoldings = toNumberSafe(existingDirect.tokenHoldings, 0) + remainingAmount;
            }
          }
        } else {
          // This is a sub-investor (has custodianId and primarySubscriberId)
          // The amount is already aggregated above, but we need to track the custodian
          const custodianKey = String(custodianId);
          const existingCustodian = ps.custodians.find(c => c.investorId === custodianKey);
          
          if (!existingCustodian) {
            // Find custodian info from all holdings
            const custodianHolding = allHoldingsArray.find(h => {
              const hInvestorId = h.investorId?._id || h.investorId;
              const hHolderId = h.holderId?._id || h.holderId;
              return String(hInvestorId) === custodianKey || String(hHolderId) === custodianKey;
            });
            
            const custodianSnapshot = custodianHolding?.investorSnapshot || custodianHolding?.holderSnapshot || {};
            ps.custodians.push({
              investorId: custodianKey,
              name: custodianSnapshot.name || custodianSnapshot.email || 'Unknown Custodian',
              email: custodianSnapshot.email || '—',
              tokenHoldings: remainingAmount, // Will be aggregated as we process more sub-investors
              isCustodian: true
            });
          } else {
            // Aggregate sub-investor amounts under custodian
            existingCustodian.tokenHoldings = toNumberSafe(existingCustodian.tokenHoldings, 0) + remainingAmount;
          }
        }
      }
    }
  });

  // Convert map to array and return only Primary Subscribers
  return Array.from(primarySubscriberMap.values());
};

const MaturityManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadContext, setUploadContext] = useState(null);
  const [detailsContext, setDetailsContext] = useState(null);
  const [updateDateContext, setUpdateDateContext] = useState(null);
  const [newMaturityDate, setNewMaturityDate] = useState('');
  const [paymentReferenceContext, setPaymentReferenceContext] = useState(null);
  const [bulkPaymentReferenceContext, setBulkPaymentReferenceContext] = useState(null);
  const [proofOfSettlementFile, setProofOfSettlementFile] = useState(null);
  const [rentasId, setRentasId] = useState('');

  // Fetch maturity payment schedules
  const {
    data: maturitySchedulesRaw,
    isLoading,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['facility-agent-maturity-payment-schedules'],
    queryFn: async () => {
      try {
        const response = await maturityPaymentSchedulesAPI.getMaturityPaymentSchedules();
        // Handle nested response structure: response.data.data.data (schedules array)
        // Backend returns: { success: true, data: { data: schedules, total, ... } }
        const root = response?.data || {};
        let schedules = [];
        
        // Try different response structures
        if (root.success && root.data) {
          // Standard response: { success: true, data: { data: [...], total, ... } }
          if (Array.isArray(root.data.data)) {
            schedules = root.data.data;
          } else if (Array.isArray(root.data)) {
            schedules = root.data;
          }
        } else if (root.data && Array.isArray(root.data)) {
          schedules = root.data;
        } else if (root.data && root.data.data && Array.isArray(root.data.data)) {
          schedules = root.data.data;
        } else if (Array.isArray(root)) {
          schedules = root;
        } else if (root.schedules && Array.isArray(root.schedules)) {
          schedules = root.schedules;
        }
        
        console.log('📊 Maturity payment schedules extracted:', schedules.length);
        return Array.isArray(schedules) ? schedules : [];
      } catch (error) {
        console.error('❌ Error fetching maturity payment schedules:', error);
      return [];
    }
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Ensure maturitySchedules is always an array
  const maturitySchedules = Array.isArray(maturitySchedulesRaw) ? maturitySchedulesRaw : [];

  const queryClient = useQueryClient();

  // Mutation for updating maturity date
  const updateMaturityDateMutation = useMutation({
    mutationFn: async ({ scheduleId, maturityDate }) => {
      return await maturityPaymentSchedulesAPI.updateMaturityDate(scheduleId, maturityDate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['facility-agent-maturity-payment-schedules']);
      setUpdateDateContext(null);
      setNewMaturityDate('');
      toast.success('Maturity date updated successfully');
    },
    onError: (error) => {
      console.error('Error updating maturity date:', error);
      toast.error('Failed to update maturity date. Please try again.');
    }
  });

  // Fetch investors for a specific schedule when details are opened
  const { data: scheduleInvestors, isLoading: investorsLoading } = useQuery({
    queryKey: ['maturity-payment-investors', detailsContext?.scheduleId],
    queryFn: async () => {
      if (!detailsContext?.scheduleId) return [];
      const response = await maturityPaymentSchedulesAPI.getMaturityPaymentInvestors(detailsContext.scheduleId);
      const root = response?.data || {};
      return root.data || root || [];
    },
    enabled: !!detailsContext?.scheduleId
  });

  // Filter schedules based on search term (bond name and ISIN only)
  const filteredSchedules = useMemo(() => {
    // Ensure maturitySchedules is always an array
    const schedules = Array.isArray(maturitySchedules) ? maturitySchedules : [];
    
    if (!searchTerm.trim()) return schedules;
    const lower = searchTerm.toLowerCase();
    return schedules.filter((schedule) => {
      const bond = schedule.bond || schedule.bondId || {};
    return (
        bond.bondName?.toLowerCase().includes(lower) ||
        bond.isin?.toLowerCase().includes(lower)
      );
    });
  }, [maturitySchedules, searchTerm]);

  const downloadTemplate = () => {
    const csvContent = [
      'Investor ID,LEI,Payment Amount',
      '507f1f77bcf86cd799439011,XYZABC1234567890WXYZ,1000000.00',
      '507f1f77bcf86cd799439012,ABCXYZ9876543210LMNO,1500000.00',
      '507f1f77bcf86cd799439013,LMNOPQ1122334455RRTT,2500000.00',
      '# Note: Use actual Investor User IDs (24-char ObjectId)',
      '# LEI must be exactly 20 characters (Legal Entity Identifier)',
      '# Payment Amount should be in the bond currency',
      '# Total payment amount should match the bond issue amount'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'maturity_payment_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading maturity payment schedules..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maturity Management</h1>
              <p className="mt-2 text-sm text-gray-600">Manage maturity payment to investors</p>
            </div>
            <div className="flex items-center gap-3">
              {isFetching && <span className="text-xs text-gray-500">Refreshing...</span>}
              <button
                onClick={downloadTemplate}
                className="flex items-center space-x-2 btn-primary"
              >
                <Download className="w-4 h-4" />
                <span>Download Template</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
              {/* Search */}
        <div className="mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-6 py-4">
                <div className="relative">
              <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                  <input
                    type="text"
                className="w-full pl-10 form-input"
                placeholder="Search by bond name or ISIN"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
            </div>
                </div>
              </div>

        {/* Maturity Payment Schedules */}
        <div className="space-y-6">
          {(!Array.isArray(filteredSchedules) || filteredSchedules.length === 0) && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="px-6 py-12 text-center">
                <div className="flex flex-col items-center text-gray-500">
                  <FileText className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No maturity payment schedules found</p>
                  <p className="text-sm">Maturity payments are automatically generated when bonds reach issuance date.</p>
              </div>
            </div>
            </div>
          )}

          {Array.isArray(filteredSchedules) && filteredSchedules.map((schedule) => (
            <BondScheduleCard 
              key={schedule._id || schedule.bond?._id} 
              schedule={schedule}
              onSetUploadContext={setUploadContext}
              onSetDetailsContext={setDetailsContext}
              onSetUpdateDateContext={setUpdateDateContext}
              onSetNewMaturityDate={setNewMaturityDate}
              onSetPaymentReferenceContext={setPaymentReferenceContext}
              onSetBulkPaymentReferenceContext={setBulkPaymentReferenceContext}
            />
          ))}
          </div>
        </div>

      {/* Upload CSV Modal */}
      {uploadContext && (
        <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
          <div className="relative w-full max-w-2xl p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
            <div className="mt-3">
              <div className="flex items-center justify-between pb-4 mb-4">
                <h3 className="text-lg font-medium text-gray-900">Upload CSV for Maturity Payment</h3>
                <button
                  onClick={() => {
                    setUploadContext(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mb-4 border-b border-gray-200"></div>
                      <div className="mb-4">
                <p className="mb-2 text-sm text-gray-600">
                  <strong>Bond:</strong> {uploadContext.bond?.bondName || 'Unknown Bond'}
                </p>
                <p className="mb-4 text-sm text-gray-600">
                  <strong>Maturity Date:</strong> {uploadContext.maturityDate ? formatDate(uploadContext.maturityDate) : '—'}
                </p>
                <div className="p-3 mb-4 rounded bg-blue-50">
                  <p className="text-sm text-blue-800">
                    Upload a CSV file with investor payment allocations. The CSV should include Investor ID, LEI, and Payment Amount.
                            </p>
                          </div>
                <div className="mt-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                        </div>
                        </div>
              <div className="flex justify-end space-x-2">
                        <button
                  onClick={() => setUploadContext(null)}
                          className="btn-secondary"
                        >
                  Cancel
                        </button>
                                  <button
                  disabled
                  className="opacity-50 cursor-not-allowed btn-primary"
                >
                  Upload CSV
                                  </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Maturity Date Modal */}
      {updateDateContext && (
        <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
          <div className="relative w-full max-w-md p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
            <div className="mt-3">
              <div className="flex items-center justify-between pb-4 mb-4">
                <h3 className="text-lg font-medium text-gray-900">Update Maturity Date</h3>
                                <button
                                  onClick={() => {
                    setUpdateDateContext(null);
                    setNewMaturityDate('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                                </button>
              </div>
              <div className="mb-4 border-b border-gray-200"></div>
              <div className="mb-4">
                <p className="mb-2 text-sm text-gray-600">
                  <strong>Bond:</strong> {updateDateContext.bond?.bondName || 'Unknown Bond'}
                </p>
                <p className="mb-4 text-sm text-gray-600">
                  <strong>Current Maturity Date:</strong> {updateDateContext.currentMaturityDate ? formatDate(updateDateContext.currentMaturityDate) : '—'}
                </p>
                <div className="mt-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    New Maturity Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newMaturityDate}
                    onChange={(e) => setNewMaturityDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                                  <button
                  onClick={() => {
                    setUpdateDateContext(null);
                    setNewMaturityDate('');
                  }}
                  className="btn-secondary"
                  disabled={updateMaturityDateMutation.isLoading}
                >
                  Cancel
                                  </button>
                                <button
                                  onClick={() => {
                    if (!newMaturityDate) {
                      alert('Please select a maturity date');
                      return;
                    }
                    updateMaturityDateMutation.mutate({
                      scheduleId: updateDateContext.scheduleId,
                      maturityDate: newMaturityDate
                    });
                  }}
                  className="btn-primary"
                  disabled={updateMaturityDateMutation.isLoading || !newMaturityDate}
                >
                  {updateMaturityDateMutation.isLoading ? 'Updating...' : 'Update Date'}
                                </button>
                                </div>
                              </div>
                      </div>
                </div>
              )}

      {/* Details Modal */}
      {detailsContext && (
        <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
          <div className="relative w-full max-w-4xl p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
            <div className="mt-3">
              <div className="flex items-center justify-between pb-4 mb-4">
                <h3 className="text-lg font-medium text-gray-900">Maturity Payment Details</h3>
                <button
                  onClick={() => setDetailsContext(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mb-6 border-b border-gray-200"></div>
              
              {investorsLoading ? (
                <LoadingSpinner size="md" text="Loading investors..." />
              ) : (
                <div>
                  <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
                <div>
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Bond Information</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs text-gray-500">Bond Name</dt>
                          <dd className="text-sm text-gray-900">{detailsContext.bond?.bondName || '—'}</dd>
                    </div>
                    <div>
                          <dt className="text-xs text-gray-500">ISIN</dt>
                          <dd className="text-sm text-gray-900">{detailsContext.bond?.isin || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Maturity Date</dt>
                          <dd className="text-sm text-gray-900">
                            {detailsContext.schedule?.maturityDate ? formatDate(detailsContext.schedule.maturityDate) : '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
                  <div>
                      <h4 className="mb-3 text-sm font-medium text-gray-900">Payment Information</h4>
                    <dl className="space-y-2">
                      <div>
                          <dt className="text-xs text-gray-500">Total Payment Amount</dt>
                      <dd className="text-sm text-gray-900">
                            {formatCurrency(
                              toNumberSafe(
                                detailsContext.schedule?.totalPaymentAmount || 
                                detailsContext.bond?.issueAmount || 
                                0
                              ),
                              detailsContext.bond?.currency || 'MYR'
                            )}
                      </dd>
                    </div>
                    <div>
                          <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-900">
                            {renderStatusBadge(detailsContext.schedule)}
                      </dd>
                    </div>
                    <div>
                          <dt className="text-xs text-gray-500">Investors</dt>
                          <dd className="text-sm text-gray-900">{scheduleInvestors?.length || 0}</dd>
                    </div>
                  </dl>
                </div>
              </div>

                  {scheduleInvestors && scheduleInvestors.length > 0 && (
                      <div>
                      <h4 className="mb-3 text-sm font-medium text-gray-900">Investors</h4>
                      <div className="overflow-x-auto">
                        <table className="table">
                          <thead className="table-header">
                            <tr>
                              <th className="table-header-cell">Investor</th>
                              <th className="table-header-cell">Email</th>
                              <th className="table-header-cell">Payment Amount</th>
                              <th className="table-header-cell">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {scheduleInvestors.map((investor) => (
                              <tr key={investor.investorId || investor._id}>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {investor.name || investor.corporateInfo?.companyName || 'Unnamed'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                  {investor.email || '—'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {formatCurrency(
                                    toNumberSafe(investor.paymentAmount || investor.amount || 0),
                                    detailsContext.bond?.currency || 'MYR'
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    investor.status === 'approved' ? 'bg-green-100 text-green-800' :
                                    investor.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {investor.status || 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                </div>
              </div>
                  )}
        </div>
      )}

              <div className="flex justify-end mt-6">
                    <button
                  onClick={() => setDetailsContext(null)}
                  className="btn-secondary"
                >
                  Close
                    </button>
                  </div>
                  </div>
                  </div>
                  </div>
      )}

      {/* Payment Reference Modal */}
      {paymentReferenceContext && (
        <PaymentReferenceModal
          context={paymentReferenceContext}
          onClose={() => {
            setPaymentReferenceContext(null);
            setProofOfSettlementFile(null);
            setRentasId('');
          }}
          onSuccess={() => {
            setPaymentReferenceContext(null);
            setProofOfSettlementFile(null);
            setRentasId('');
            refetch();
          }}
        />
      )}

      {/* Bulk Payment Reference Modal */}
      {bulkPaymentReferenceContext && (
        <BulkPaymentReferenceModal
          context={bulkPaymentReferenceContext}
          onClose={() => {
            setBulkPaymentReferenceContext(null);
          }}
          onSuccess={() => {
            setBulkPaymentReferenceContext(null);
            refetch();
          }}
        />
      )}
                        </div>
                      );
};

// Payment Reference Modal Component
const PaymentReferenceModal = ({ context, onClose, onSuccess }) => {
  const [proofOfSettlementFile, setProofOfSettlementFile] = useState(null);
  const [rentasId, setRentasId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { schedule, investor, bond } = context;

  const canSubmit = proofOfSettlementFile || rentasId.trim();

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Please provide either proof of settlement or RENTAS reference');
      return;
    }

    try {
      setIsSubmitting(true);

      // Store payment reference at PS level and set status to pending_l2
      // Distribution requests will be created only after L2 approval by another FA
      const paymentRefFormData = new FormData();
      if (proofOfSettlementFile) {
        paymentRefFormData.append('proofOfSettlement', proofOfSettlementFile);
      }
      if (rentasId.trim()) {
        paymentRefFormData.append('rentasId', rentasId.trim());
      }
      paymentRefFormData.append('paymentAmount', investor.paymentAmount || 0);

      // submitPaymentReference already sets status to 'pending_l2' in the backend
      const response = await maturityPaymentSchedulesAPI.submitPaymentReference(
        schedule._id,
        investor.investorId || investor._id,
        paymentRefFormData
      );

      console.log('Payment reference submitted, response:', response?.data);

      toast.success('Payment reference submitted successfully. Waiting for L2 approval by another FA account.');

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries(['facility-agent-maturity-payment-schedules']);
      queryClient.invalidateQueries(['bond-holdings-maturity-all', bond._id || schedule.bondId]);
      
      // Wait a bit for the backend to process and ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      onSuccess();
    } catch (error) {
      console.error('Error submitting payment reference:', error);
      toast.error(error?.response?.data?.message || 'Failed to submit payment reference');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative w-full max-w-2xl p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
        <div className="mt-3">
          <div className="flex items-center justify-between pb-4 mb-4">
            <h3 className="text-lg font-medium text-gray-900">Add Payment Reference</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="mb-4 border-b border-gray-200"></div>

          <div className="mb-4">
            <p className="mb-2 text-sm text-gray-600">
              <strong>Primary Subscriber:</strong> {investor.name || 'Unknown'}
            </p>
            <p className="mb-2 text-sm text-gray-600">
              <strong>Payment Amount:</strong> {formatCurrency(investor.paymentAmount || 0, bond?.currency || 'MYR')}
            </p>
            <p className="mb-4 text-xs text-gray-500">
              Distribution requests will be created for Direct Investors and Custodians under this Primary Subscriber.
            </p>
          </div>

          {/* Proof of Settlement - File Upload or RENTAS reference */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-900">Proof of Settlement *</h4>
            <p className="mb-3 text-xs text-gray-500">
              Provide either a proof of settlement file, RENTAS reference, or both. At least one is required.
            </p>
            
            {/* File Upload Section */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Upload File (Optional)
              </label>
              <div className="p-4 border-2 border-gray-300 border-dashed rounded-lg">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.type !== 'application/pdf') {
                        toast.error('Please upload a PDF file only.');
                        e.target.value = '';
                        return;
                      }
                      if (file.size > 20 * 1024 * 1024) {
                        toast.error('File size must be less than 20MB.');
                        e.target.value = '';
                        return;
                      }
                      setProofOfSettlementFile(file);
                    }
                  }}
                  className="hidden"
                  id="proof-of-settlement-upload"
                />
                <label
                  htmlFor="proof-of-settlement-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Click to upload PDF file or drag and drop
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    PDF only, max 20MB
                  </span>
                </label>
                {proofOfSettlementFile && (
                  <div className="flex items-center justify-between p-3 mt-3 border border-green-200 rounded bg-green-50">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-green-600" />
                      <div>
                        <span className="text-sm font-medium text-green-800">
                          {proofOfSettlementFile.name}
                        </span>
                        <span className="ml-2 text-xs text-green-600">
                          ({(proofOfSettlementFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProofOfSettlementFile(null);
                        document.getElementById('proof-of-settlement-upload').value = '';
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* RENTAS reference Input Section */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                RENTAS reference (Optional)
              </label>
              <input
                type="text"
                value={rentasId}
                onChange={(e) => setRentasId(e.target.value)}
                placeholder="Enter RENTAS reference"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the RENTAS transaction ID as proof of settlement.
              </p>
            </div>

            {/* Validation message */}
            {!proofOfSettlementFile && !rentasId?.trim() && (
              <p className="mt-2 text-xs text-red-600">
                * At least one proof method (file upload or RENTAS reference) is required.
              </p>
            )}
          </div>

          <div className="flex justify-end mt-6 space-x-2">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className={`btn-primary ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit for Approval'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Separate component for each bond schedule to allow hooks usage
const BondScheduleCard = ({ schedule, onSetUploadContext, onSetDetailsContext, onSetUpdateDateContext, onSetNewMaturityDate, onSetPaymentReferenceContext, onSetBulkPaymentReferenceContext }) => {
  const bond = schedule.bond || {};
  const bondId = bond._id || bond.id;
  const csvInvestors = schedule.investors || [];
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id;
  
  // State to store editable payment amounts (keyed by investorId)
  const [paymentAmounts, setPaymentAmounts] = useState({});
  
  // State to store distribution request data for each investor (to check L1 approver)
  const [distributionRequests, setDistributionRequests] = useState({});
  
  // Mutation for L2 approval (creates distribution requests with pending_l1 status)
  // After L2 approval, distribution requests are created for Direct Investors and Custodians
  // Requests start with pending_l1 status and need L1 approval, then L2 approval
  // Payment amounts are calculated proportionally: (PS Payment Amount) × (Recipient Tokens) / (Total Tokens under PS)
  const approveL2Mutation = useMutation({
    mutationFn: async (investorId) => {
      return await maturityPaymentSchedulesAPI.approveL2AndCreateRequest(schedule._id, investorId);
    },
    onSuccess: (response) => {
      const requestCount = response?.data?.requestCount || response?.data?.distributionRequests?.length || 1;
      toast.success(`L2 approved. Created ${requestCount} distribution request(s) with pending_l1 status for Direct Investors and Custodians under this Primary Subscriber.`);
      // Refetch schedules to update the UI
      queryClient.invalidateQueries(['facility-agent-maturity-payment-schedules']);
      queryClient.invalidateQueries(['bond-holdings-maturity-all', bondId]);
      // Clear distribution requests cache
      setDistributionRequests({});
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to approve L2');
    }
  });
  
  // Fetch distribution requests for investors with pending_l2 status
  const fetchDistributionRequest = async (investorId) => {
    if (distributionRequests[investorId]) {
      return distributionRequests[investorId];
    }
    
    try {
      const response = await maturityDistributionRequestsAPI.getMyRequests({
        maturityPaymentScheduleId: schedule._id,
        holderId: investorId
      });
      const requests = response.data?.data || response.data || [];
      const request = Array.isArray(requests) ? requests[0] : null;
      
      if (request) {
        setDistributionRequests(prev => ({
          ...prev,
          [investorId]: request
        }));
        return request;
      }
      return null;
    } catch (error) {
      console.error('Error fetching distribution request:', error);
      return null;
    }
  };
  
  // Fetch holdings for this bond
  // We fetch 'all' to get custodian info from ended holdings, then filter to active for processing
  const { data: allHoldingsData = [], isLoading: holdingsLoading } = useQuery({
    queryKey: ['bond-holdings-maturity-all', bondId],
    queryFn: async () => {
      if (!bondId) return [];
      try {
        const response = await holdingsAPI.getHoldingsByBond(bondId, { status: 'all' });
        const root = response?.data || {};
        let holdings = [];
        
        if (Array.isArray(root.data)) {
          holdings = root.data;
        } else if (Array.isArray(root)) {
          holdings = root;
        }
        
        return Array.isArray(holdings) ? holdings : [];
      } catch (error) {
        console.error('Error fetching holdings:', error);
        return [];
      }
    },
    enabled: !!bondId,
    staleTime: 30000 // Cache for 30 seconds
  });

  // Filter to active holdings for processing
  const holdingsData = useMemo(() => {
    return allHoldingsData.filter(holding => {
      const remaining = toNumberSafe(holding.remainingTokenAmount || 0, 0);
      const hasEndDate = !!holding.holdingsEndDate;
      return remaining > 0 && !hasEndDate;
    });
  }, [allHoldingsData]);

  // Process holdings to get investors
  // Pass all holdings (including inactive) to get custodian info, but only process active ones
  const holdingsInvestors = useMemo(() => {
    return processHoldingsToInvestors(allHoldingsData, holdingsData);
  }, [allHoldingsData, holdingsData]);

  // Calculate total active tokens for the bond (denominator for payment calculation)
  const totalActiveTokens = useMemo(() => {
    return holdingsData.reduce((sum, holding) => {
      return sum + toNumberSafe(holding.remainingTokenAmount || 0, 0);
    }, 0);
  }, [holdingsData]);

  // Parse Decimal128 values from MongoDB
  const totalPaymentAmount = toNumberSafe(
    schedule.totalPaymentAmount || schedule.bond?.issueAmount || bond.issueAmount,
    0
  );

  // Process Primary Subscribers and calculate payment amounts
  // Primary Subscribers are shown, with payment amounts aggregated from direct investors and sub-investors
  const investors = useMemo(() => {
    // Start with Primary Subscribers from holdings
    const result = holdingsInvestors.map(ps => {
      const investorKey = String(ps.investorId);
      
      // Calculate payment amount based on aggregated token holdings
      // Formula: (totalPaymentAmount * psTokenHoldings) / totalActiveTokens
      let calculatedAmount = 0;
      if (totalActiveTokens > 0 && totalPaymentAmount > 0 && ps.tokenHoldings) {
        calculatedAmount = (totalPaymentAmount * ps.tokenHoldings) / totalActiveTokens;
      }
      
      // Use edited amount if available, otherwise use calculated amount
      const finalAmount = paymentAmounts[investorKey] !== undefined 
        ? paymentAmounts[investorKey]
        : calculatedAmount;
      
      return {
        ...ps,
        paymentAmount: finalAmount,
        // Keep directInvestors and custodians arrays for submission
        directInvestors: ps.directInvestors || [],
        custodians: ps.custodians || [],
        // Preserve paymentReferenceSubmittedBy from schedule if available
        paymentReferenceSubmittedBy: ps.paymentReferenceSubmittedBy
      };
    });

    // Merge with CSV investors if they exist (for backward compatibility)
    // CSV investors that match Primary Subscribers will override holdings data
    csvInvestors.forEach(csvInv => {
      const csvId = csvInv.investorId?._id || csvInv.investorId || csvInv._id;
      if (!csvId) return;
      
      const csvKey = String(csvId);
      const existingPS = result.find(ps => String(ps.investorId) === csvKey);
      
      if (existingPS) {
        // Merge CSV data with PS data
        const investorSnapshot = csvInv.investorSnapshot;
        const populatedInvestor = csvInv.investorId && typeof csvInv.investorId === 'object' && csvInv.investorId._id 
          ? csvInv.investorId 
          : null;
        
        existingPS.name = investorSnapshot?.name || csvInv.name || populatedInvestor?.name || existingPS.name;
        existingPS.email = investorSnapshot?.email || csvInv.email || populatedInvestor?.email || existingPS.email;
        existingPS.status = csvInv.status || existingPS.status;
        // Preserve paymentReferenceSubmittedBy from CSV investor (from schedule)
        if (csvInv.paymentReferenceSubmittedBy) {
          existingPS.paymentReferenceSubmittedBy = csvInv.paymentReferenceSubmittedBy;
        }
        
        // Use CSV payment amount if provided, otherwise keep calculated amount
        if (csvInv.paymentAmount || csvInv.amount) {
          const csvAmount = toNumberSafe(csvInv.paymentAmount || csvInv.amount, 0);
          existingPS.paymentAmount = paymentAmounts[csvKey] !== undefined 
            ? paymentAmounts[csvKey]
            : csvAmount;
        }
      }
    });

    return result;
  }, [holdingsInvestors, csvInvestors, totalActiveTokens, totalPaymentAmount, paymentAmounts]);

  const maturityDate = schedule.maturityDate || bond.maturityDate;

  // Check if maturity date has been reached (today or in the past)
  const isMaturityDateReached = useMemo(() => {
    if (!maturityDate) return false;
    const maturity = new Date(maturityDate);
    const today = new Date();
    // Set both dates to midnight for accurate day comparison
    maturity.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return maturity <= today;
  }, [maturityDate]);

                    return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Bond Header */}
      <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900">{bond.bondName || 'Unnamed Bond'}</h3>
                        {bond.tokenDetails?.symbol && (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-800 rounded">
                            {bond.tokenDetails.symbol}
                          </span>
                        )}
                            </div>
                      <div className="flex items-center mt-1 space-x-4 text-sm text-gray-500">
                        <span>ISIN: {bond.isin || '—'}</span>
                        <span>•</span>
                     
                        <span>{bond.productType === "Fixed Notes" ? "Conventional" : (bond.productType || '—')}</span>
                        </div>
                      </div>
                    <div className="flex items-center space-x-3">
                      
                  <button
                        className="flex items-center space-x-1 btn-primary btn-sm"
                        
                    onClick={() => {
                          onSetUpdateDateContext({
                            scheduleId: schedule._id,
                            bond: bond,
                            schedule: schedule,
                            currentMaturityDate: schedule.maturityDate || bond.maturityDate
                          });
                          // Format date for input (YYYY-MM-DD)
                          const currentDate = schedule.maturityDate || bond.maturityDate;
                          if (currentDate) {
                            const date = new Date(currentDate);
                            const formattedDate = date.toISOString().split('T')[0];
                            onSetNewMaturityDate(formattedDate);
                          } else {
                            onSetNewMaturityDate('');
                          }
                        }}
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        <span>Update Maturity Date</span>
                      </button>
                      {/* CSV Upload button temporarily hidden */}
                      {/* <button
                        className="flex items-center space-x-1 btn-secondary btn-sm"
                        onClick={() => {
                          onSetUploadContext(schedule);
                        }}
                      >
                        <Upload className="w-3.5 h-3.5" />
                        <span>Upload CSV</span>
                      </button> */}
                      <button
                        className="flex items-center space-x-1 btn-outline btn-sm"
                        onClick={() => onSetDetailsContext({ scheduleId: schedule._id, bond, schedule })}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Details</span>
                  </button>
                </div>
            </div>
          </div>

      {/* Maturity Payment Summary */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                    <div>
                      <span className="text-gray-600">Maturity Date:</span>
                      <div className="flex items-center mt-1 font-medium">
                        <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                        {maturityDate ? formatDate(maturityDate) : '—'}
              </div>
              </div>
                    <div>
                      <span className="text-gray-600">Total Payment Amount:</span>
                      <div className="flex items-center mt-1 font-medium">
                        <DollarSign className="w-4 h-4 mr-1 text-gray-400" />
                        {formatCurrency(totalPaymentAmount, bond.currency || 'MYR')}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">Bond issue amount</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Primary Subscribers:</span>
                      <div className="flex items-center mt-1 font-medium">
                        <Users className="w-4 h-4 mr-1 text-gray-400" />
                        {investors.length}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">Total Primary Subscribers</div>
                    </div>
                  </div>
      </div>

      {/* Investors Table */}
      {investors.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
                      <div className="text-sm text-gray-600">
                        {investors.length} Primary Subscriber{investors.length > 1 ? 's' : ''} found
                      </div>
                <button
                        onClick={() => {
                          onSetBulkPaymentReferenceContext({
                            schedule,
                            investors: investors.filter(inv => {
                              const invStatus = inv.status || 'pending';
                              return invStatus !== 'approved' && invStatus !== 'pending_l2' && invStatus !== 'pending_request';
                            }),
                            bond
                          });
                        }}
                        disabled={!isMaturityDateReached || investors.filter(inv => {
                          const invStatus = inv.status || 'pending';
                          return invStatus !== 'approved' && invStatus !== 'pending_l2' && invStatus !== 'pending_request';
                        }).length === 0}
                        className={`btn-primary btn-sm flex items-center gap-1 ${!isMaturityDateReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={!isMaturityDateReached ? 'Maturity date has not been reached yet' : 'Add payment reference for all Primary Subscribers'}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Bulk Add Payment Reference</span>
                </button>
              </div>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead className="table-header">
                          <tr>
                            <th className="table-header-cell">Primary Subscriber</th>
                            <th className="table-header-cell">Email</th>
                            <th className="table-header-cell">Payment Amount</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {investors.map((investor) => {
                            const investorId = investor.investorId || investor.userId || investor._id;
                            const isApproved = investor.status === 'approved' || investor.approved;

                            return (
                              <tr key={investorId} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {investor.name || investor.corporateInfo?.companyName || 'Unnamed'}
                                        {investor.isPrimarySubscriber && (
                                          <span className="ml-2 text-xs text-gray-500">
                                            (Primary Subscriber)
                                          </span>
                                        )}
                                      </div>
                                      {(investor.directInvestors && investor.directInvestors.length > 0) || (investor.custodians && investor.custodians.length > 0) ? (
                                        <div className="mt-1 text-xs text-gray-500">
                                          {investor.directInvestors && investor.directInvestors.length > 0 && (
                                            <span>
                                              {investor.directInvestors.length} direct investor{investor.directInvestors.length !== 1 ? 's' : ''}
                                            </span>
                                          )}
                                          {investor.directInvestors && investor.directInvestors.length > 0 && investor.custodians && investor.custodians.length > 0 && (
                                            <span className="mx-1">•</span>
                                          )}
                                          {investor.custodians && investor.custodians.length > 0 && (
                                            <span>
                                              {investor.custodians.length} custodian{investor.custodians.length !== 1 ? 's' : ''}
                                            </span>
                                          )}
                                        </div>
                                      ) : null}
                                      <div className="mt-1 font-mono text-xs text-gray-500">ID: {investorId}</div>
          </div>
                                    {isApproved && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Approved
                                      </span>
                                    )}
        </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                  {investor.email || '—'}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={investor.paymentAmount || 0}
                                      onChange={(e) => {
                                        const newValue = parseFloat(e.target.value) || 0;
                                        setPaymentAmounts(prev => ({
                                          ...prev,
                                          [investorId]: newValue
                                        }));
                                      }}
                                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                      disabled={isApproved}
                                    />
                                    <span className="text-xs text-gray-500">
                                      {bond.currency || 'MYR'}
                                    </span>
                                  </div>
                                  {investor.tokenHoldings > 0 && (
                                    <div className="mt-1 text-xs text-gray-400">
                                      {investor.tokenHoldings.toLocaleString()} tokens
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  {investor.status ? (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      investor.status === 'approved' ? 'bg-green-100 text-green-800' :
                                      investor.status === 'pending_l2' ? 'bg-orange-100 text-orange-800' :
                                      investor.status === 'pending_request' ? 'bg-green-100 text-green-800' :
                                      investor.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {investor.status === 'pending_l2' ? 'Pending L2' : 
                                       investor.status === 'pending_request' ? 'Approved' :
                                       investor.status === 'approved' ? 'Approved' :
                                       investor.status}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  {investor.status === 'approved' || investor.status === 'pending_request' ? (
                                    // Empty action column after L2 approval
                                    <div></div>
                                  ) : investor.status === 'pending_l2' ? (
                                    <PendingL2Button
                                      investorId={investorId}
                                      scheduleId={schedule._id}
                                      approveL2Mutation={approveL2Mutation}
                                      isMaturityDateReached={isMaturityDateReached}
                                      currentUserId={currentUserId}
                                      paymentReferenceSubmittedBy={investor.paymentReferenceSubmittedBy}
                                    />
                                  ) : (
                <button
                                      onClick={() => {
                                        onSetPaymentReferenceContext({
                                          schedule,
                                          investor,
                                          bond
                                        });
                                      }}
                                      
                                      className={`btn-primary btn-sm flex items-center gap-1 ${!isMaturityDateReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title={!isMaturityDateReached ? 'Maturity date has not been reached yet' : 'Add payment reference (proof of settlement or RENTAS ID)'}
                                    >
                                      <FileText className="w-3.5 h-3.5" />
                                      Add Payment Reference
                </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
              </div>
                  </>
                ) : (
                  <div className="px-6 py-12 text-center bg-white-50">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="mb-1 text-sm font-medium text-gray-900">No Primary Subscribers Found</h3>
                    <p className="text-xs text-gray-600">
                      No Primary Subscribers found for this bond. Primary Subscribers are identified from bond holdings.
                    </p>
        </div>
      )}
    </div>
  );
};

// Bulk Payment Reference Modal Component
const BulkPaymentReferenceModal = ({ context, onClose, onSuccess }) => {
  const [proofOfSettlementFile, setProofOfSettlementFile] = useState(null);
  const [rentasId, setRentasId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { schedule, investors, bond } = context;

  const canSubmit = proofOfSettlementFile || rentasId.trim();
  const eligibleInvestors = investors.filter(inv => {
    const invStatus = inv.status || 'pending';
    return invStatus !== 'approved' && invStatus !== 'pending_l2' && invStatus !== 'pending_request';
  });

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Please provide either proof of settlement or RENTAS reference');
      return;
    }

    if (eligibleInvestors.length === 0) {
      toast.error('No eligible Primary Subscribers to process');
      return;
    }

    try {
      setIsSubmitting(true);

      // Store payment references for all PS - submitPaymentReference already sets status to 'pending_l2' in the backend
      // Distribution requests will be created only after L2 approval by another FA
      for (const ps of eligibleInvestors) {
        const paymentRefFormData = new FormData();
        if (proofOfSettlementFile) {
          paymentRefFormData.append('proofOfSettlement', proofOfSettlementFile);
        }
        if (rentasId.trim()) {
          paymentRefFormData.append('rentasId', rentasId.trim());
        }
        paymentRefFormData.append('paymentAmount', ps.paymentAmount || 0);

        try {
          await maturityPaymentSchedulesAPI.submitPaymentReference(
            schedule._id,
            ps.investorId || ps._id,
            paymentRefFormData
          );
        } catch (refError) {
          console.error(`Error storing payment reference for PS ${ps.investorId}:`, refError);
          toast.error(`Failed to store payment reference for ${ps.name || 'Primary Subscriber'}. Please try again.`);
          return;
        }
      }

      toast.success(`Payment reference submitted successfully for ${eligibleInvestors.length} Primary Subscriber(s). Waiting for L2 approval by another FA account.`);

      queryClient.invalidateQueries(['facility-agent-maturity-payment-schedules']);
      queryClient.invalidateQueries(['bond-holdings-maturity-all', bond._id || schedule.bondId]);
      
      // Wait a bit for the backend to process and ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSuccess();
    } catch (error) {
      console.error('Error submitting bulk payment reference:', error);
      toast.error(error?.response?.data?.message || 'Failed to submit payment reference');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative w-full max-w-2xl p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
        <div className="mt-3">
          <div className="flex items-center justify-between pb-4 mb-4">
            <h3 className="text-lg font-medium text-gray-900">Bulk Add Payment Reference</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="mb-4 border-b border-gray-200"></div>

          <div className="mb-4">
            <p className="mb-2 text-sm text-gray-600">
              <strong>Bond:</strong> {bond?.bondName || 'Unknown Bond'}
            </p>
            <p className="mb-2 text-sm text-gray-600">
              <strong>Primary Subscribers:</strong> {eligibleInvestors.length} Primary Subscriber{eligibleInvestors.length !== 1 ? 's' : ''} will be processed
            </p>
            <p className="mb-4 text-sm text-gray-500">
              Distribution requests will be created for all Direct Investors and Custodians under these Primary Subscribers.
            </p>
          </div>

          {/* Proof of Settlement - File Upload or RENTAS reference */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-900">Proof of Settlement *</h4>
            <p className="mb-3 text-xs text-gray-500">
              Provide either a proof of settlement file, RENTAS reference, or both. At least one is required. This will apply to all {eligibleInvestors.length} Primary Subscriber{eligibleInvestors.length !== 1 ? 's' : ''} and their Direct Investors and Custodians.
            </p>
            
            {/* File Upload Section */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Upload File (Optional)
              </label>
              <div className="p-4 border-2 border-gray-300 border-dashed rounded-lg">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.type !== 'application/pdf') {
                        toast.error('Please upload a PDF file only.');
                        e.target.value = '';
                        return;
                      }
                      if (file.size > 20 * 1024 * 1024) {
                        toast.error('File size must be less than 20MB.');
                        e.target.value = '';
                        return;
                      }
                      setProofOfSettlementFile(file);
                    }
                  }}
                  className="hidden"
                  id="bulk-proof-of-settlement-upload"
                />
                <label
                  htmlFor="bulk-proof-of-settlement-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Click to upload PDF file or drag and drop
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    PDF only, max 20MB
                  </span>
                </label>
                {proofOfSettlementFile && (
                  <div className="flex items-center justify-between p-3 mt-3 border border-green-200 rounded bg-green-50">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-green-600" />
                      <div>
                        <span className="text-sm font-medium text-green-800">
                          {proofOfSettlementFile.name}
                        </span>
                        <span className="ml-2 text-xs text-green-600">
                          ({(proofOfSettlementFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProofOfSettlementFile(null);
                        document.getElementById('bulk-proof-of-settlement-upload').value = '';
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* RENTAS reference Input Section */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                RENTAS reference (Optional)
              </label>
              <input
                type="text"
                value={rentasId}
                onChange={(e) => setRentasId(e.target.value)}
                placeholder="Enter RENTAS reference"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the RENTAS transaction ID as proof of settlement.
              </p>
            </div>

            {/* Validation message */}
            {!proofOfSettlementFile && !rentasId?.trim() && (
              <p className="mt-2 text-xs text-red-600">
                * At least one proof method (file upload or RENTAS reference) is required.
              </p>
            )}
          </div>

          <div className="flex justify-end mt-6 space-x-2">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting || eligibleInvestors.length === 0}
              className={`btn-primary ${!canSubmit || eligibleInvestors.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit for Approval'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Component for handling Pending L2 button - only visible to accounts other than the submitter
const PendingL2Button = ({ investorId, scheduleId, approveL2Mutation, isMaturityDateReached, currentUserId, paymentReferenceSubmittedBy }) => {
  // Check if current user is the one who submitted the payment reference
  const isSubmitter = useMemo(() => {
    if (!paymentReferenceSubmittedBy || !currentUserId) {
      // If no submitter info, allow approval (fallback for old data)
      return false;
    }
    
    // Handle both object and string ID formats
    const submitterId = paymentReferenceSubmittedBy._id || paymentReferenceSubmittedBy;
    const currentId = currentUserId._id || currentUserId;
    
    // Compare as strings to ensure accurate matching
    return String(submitterId) === String(currentId);
  }, [paymentReferenceSubmittedBy, currentUserId]);
  
  // Hide button if current user is the submitter
  if (isSubmitter) {
    return (
      <div className="text-xs text-gray-500">
        Waiting for L2 approval by another FA account
      </div>
    );
  }
  
  return (
    <button
      onClick={() => {
        approveL2Mutation.mutate(investorId);
      }}
      disabled={approveL2Mutation.isLoading || !isMaturityDateReached}
      className={`btn-primary btn-sm flex items-center gap-1 ${!isMaturityDateReached ? 'opacity-50 cursor-not-allowed' : ''}`}
      title={!isMaturityDateReached ? 'Maturity date has not been reached yet' : 'Approve L2 and create distribution request'}
    >
      {approveL2Mutation.isLoading ? (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Approving L2...
        </>
      ) : (
        <>
          <CheckCircle2 className="w-3.5 h-3.5" />
          Approve L2
        </>
      )}
    </button>
  );
};

export default MaturityManagement; 
